import { useEffect, useRef, useState } from 'react';
import { fetchPipelines, type Pipeline, type Opportunity, type Contact, type ContactCustom } from '../api';
import PipelineBoard from '../components/PipelineBoard';
import ConversationInbox from '../components/ConversationInbox';
import MaterialPanel from '../components/MaterialPanel';
import ContactDetail from '../components/ContactDetail';
import type { OpenContactDetail } from '../navigation/uiActions';
import { ScrollStrip } from '../components/ScrollStrip';

function tabBtn(active: boolean): React.CSSProperties {
    return {
        fontSize: 12,
        padding: '4px 12px',
        borderRadius: 8,
        cursor: 'pointer',
        border: active ? '1px solid rgba(52,211,153,0.5)' : '1px solid rgba(255,255,255,0.14)',
        background: active ? 'rgba(52,211,153,0.18)' : 'rgba(255,255,255,0.05)',
        color: active ? '#d1fae5' : 'inherit',
        fontWeight: active ? 600 : 400,
    };
}

/**
 * CrmView (F1) — säljtratt + unified inbox.
 * Kanban över vald pipeline (tabbar, inte rullgardin). Sökfältet filtrerar
 * korten live. Klick på ett kort öppnar detaljvyn där kontakten kan
 * redigeras/raderas manuellt. Data ägs helt av SCC — ingen GHL.
 */
export default function CrmView() {
    const [pipelines, setPipelines] = useState<Pipeline[]>([]);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selected, setSelected] = useState<Opportunity | null>(null);

    // Rundturen stänger detaljpanelen när berättelsen går vidare
    useEffect(() => {
        const onTourClose = () => setSelected(null);
        window.addEventListener('scc:tour-close-card', onTourClose);
        return () => window.removeEventListener('scc:tour-close-card', onTourClose);
    }, []);
    // Alex (navigate_ui): kortet kan ligga i en annan pipeline än den som visas.
    // Byt flik först. Boarden remontas vid flikbyte (key=activeId), så önskan
    // hålls här och skickas om till den nya boarden när den monterats.
    const pendingOpenRef = useRef<OpenContactDetail | null>(null);
    useEffect(() => {
        const onOpenContact = (e: Event) => {
            const detail = (e as CustomEvent<OpenContactDetail>).detail;
            const pid = detail?.pipelineId;
            if (!pid || pid === activeId || !pipelines.some((p) => p.id === pid)) return;
            pendingOpenRef.current = { contactId: detail.contactId, contactName: detail.contactName, pipelineId: null };
            setSelected(null);
            setActiveId(pid);
        };
        const onSelectPipeline = (e: Event) => {
            const pid = (e as CustomEvent<{ pipelineId: string }>).detail?.pipelineId;
            if (!pid || pid === activeId || !pipelines.some((p) => p.id === pid)) return;
            setSelected(null);
            setActiveId(pid);
        };
        window.addEventListener('scc:open-contact', onOpenContact);
        window.addEventListener('scc:select-pipeline', onSelectPipeline);
        return () => {
            window.removeEventListener('scc:open-contact', onOpenContact);
            window.removeEventListener('scc:select-pipeline', onSelectPipeline);
        };
    }, [pipelines, activeId]);
    useEffect(() => {
        const pending = pendingOpenRef.current;
        if (!pending) return;
        pendingOpenRef.current = null;
        const t = setTimeout(() => {
            window.dispatchEvent(new CustomEvent<OpenContactDetail>('scc:open-contact', { detail: pending }));
        }, 0);
        return () => clearTimeout(t);
    }, [activeId]);
    const [detailTab, setDetailTab] = useState<'detail' | 'inbox' | 'material'>('detail');
    const [search, setSearch] = useState('');
    const [boardVersion, setBoardVersion] = useState(0);

    useEffect(() => {
        (async () => {
            try {
                const pl = await fetchPipelines();
                setPipelines(pl);
                const def = pl.find((p) => p.is_default) ?? pl[0];
                setActiveId(def?.id ?? null);
                setError(null);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Kunde inte hämta pipelines');
            }
            setLoading(false);
        })();
    }, []);

    /** Efter redigering: spegla ändringen i öppna detaljvyn + ladda om boarden. */
    const handleSaved = (c: Contact) => {
        setSelected((prev) => prev
            ? {
                ...prev,
                title: c.name ?? prev.title,
                contact: prev.contact
                    ? { ...prev.contact, name: c.name, email: c.email, phone: c.phone, tags: c.tags, custom: c.custom as ContactCustom }
                    : prev.contact,
            }
            : prev);
        setBoardVersion((v) => v + 1);
    };

    const handleDeleted = () => {
        setSelected(null);
        setBoardVersion((v) => v + 1);
    };

    return (
        <div className="crm-view" style={{ maxWidth: 1400, margin: '0 auto' }}>
            <div className="crm-head" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>🗂️ CRM</h1>
                {pipelines.length > 1 && (
                    <ScrollStrip aktivNyckel={activeId ?? ''} aria-label="Välj CRM-lista" className="crm-pipelines">
                        {pipelines.map((p) => (
                            <button
                                key={p.id}
                                data-aktiv={p.id === activeId ? 'true' : undefined}
                                onClick={() => { setActiveId(p.id); setSelected(null); }}
                                style={tabBtn(p.id === activeId)}
                            >
                                {p.name}
                            </button>
                        ))}
                    </ScrollStrip>
                )}
                <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Sök prospekt (namn, IG, mail, tel)…"
                    style={{
                        marginLeft: 'auto', width: 260, maxWidth: '100%', flex: '1 1 180px', minWidth: 0,
                        padding: '7px 12px', borderRadius: 10, fontSize: 13,
                        border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(0,0,0,0.25)', color: 'inherit', outline: 'none',
                    }}
                />
            </div>

            {loading && <p style={{ opacity: 0.6 }}>Laddar CRM…</p>}
            {error && <p style={{ color: '#ff6b6b' }}>Fel: {error}</p>}
            {!loading && !error && !activeId && (
                <p style={{ opacity: 0.6 }}>
                    Ingen pipeline finns än. Kör migrationen <code>ticket24_pipelines.sql</code> för att
                    skapa default-pipelinen.
                </p>
            )}

            {activeId && (
                <div className="crm-layout" style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                    <div className="crm-board" style={{ flex: 1, minWidth: 0 }}>
                        <PipelineBoard
                            key={`${activeId}:${boardVersion}`}
                            pipelineId={activeId}
                            search={search}
                            onSelectContact={(opp) => { setSelected(opp); setDetailTab('detail'); }}
                        />
                    </div>
                    {selected && selected.contact && (
                        <div className="crm-detail" style={{
                            flex: '0 0 400px', position: 'sticky', top: 16,
                            // Höjdtak + flex-kolumn: utan detta växer panelen obegränsat
                            // och konversationslistan får ingen vägg att scrolla mot.
                            // Offset 210px = panelens topp-position (~197px, under CRM-flik +
                            // sök) + marginal. Tidigare 120px lät panelbotten hamna under
                            // skärmkanten så sista meddelandet klipptes.
                            display: 'flex', flexDirection: 'column',
                            maxHeight: 'calc(100dvh - 225px)',
                        }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                                <button onClick={() => setDetailTab('detail')} style={tabBtn(detailTab === 'detail')}>Detaljer</button>
                                <button onClick={() => setDetailTab('inbox')} style={tabBtn(detailTab === 'inbox')}>Konversation</button>
                                <button onClick={() => setDetailTab('material')} style={tabBtn(detailTab === 'material')}>Material</button>
                                <button
                                    onClick={() => setSelected(null)}
                                    style={{ marginLeft: 'auto', ...tabBtn(false), padding: '4px 10px' }}
                                    aria-label="Stäng"
                                >
                                    ✕
                                </button>
                            </div>
                            {/* Innehållet scrollar bara vertikalt. overflowY: auto ensamt gör
                                overflow-x till 'auto' automatiskt, så ett enda obrytbart ord i en
                                konversationsbubbla gjorde hela panelen dragbar i sidled och texten
                                hamnade utanför skärmkanten. */}
                            <div className="crm-detail-body" style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                                {detailTab === 'detail'
                                    ? <ContactDetail opportunity={selected} onSaved={handleSaved} onDeleted={handleDeleted} />
                                    : detailTab === 'inbox'
                                        ? <ConversationInbox contactId={selected.contact.id} title={selected.title} />
                                        : <MaterialPanel contactId={selected.contact.id} />}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
